import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { AutoComplete, Page } from "@/model";
import { clickFromKeyboard } from "@/util/keyboardUtil";
import { useDeleteAutocomplete, useDeleteAutocompletes } from "@/query/autocomplete";
import { useAgentId, useDialog, useModal, useRouteHandler, useSelection } from "@/hook";
import { errorToast, successToast } from "@/component/Toast";
import { Popover, PopoverButton, PopoverItem, PopoverMenu } from "@/component/Popover";
import { IndeterminateCheckbox } from "@/component/input";
import HighlightText from "@/component/HighlightText";
import SelectionActionBar from "@/component/SelectionActionBar";
import { DeleteIcon, EditIcon } from "@/assets/svgs/icons";

import { AutocompletePageParam } from "../AutocompleteView";
import AutocompleteModifyModal, { AUTOCOMPLETE_MODIFY_MODAL_ID } from "./AutocompleteModifyModal";


interface AutocompleteTableProps {
    page?: Page<AutoComplete>;
    pageParam: AutocompletePageParam;
}

const AutocompleteTable: React.FC<AutocompleteTableProps> = (props) => {
    const { page, pageParam } = props;
    
    const intl = useIntl();
    const agentId = useAgentId();
    const dialog = useDialog();

    const { mutateAsync: deleteAutocomplete } = useDeleteAutocomplete(agentId);
    const { mutateAsync: deleteAutocompletes } = useDeleteAutocompletes(agentId);

    const pageIds = useMemo(() => page?.content.map(autocomplete => autocomplete.id) ?? [], [page?.content]);

    const { selectionCount, isAllSelected, pageSelectionStatus, selectedIds, markOne, markPage, markAll, clearSelection } = useSelection({
        targetIds: pageIds,
        total: page?.total ?? 0,
    });

    const deleteOne = async (id: string) => {
        await deleteAutocomplete(id)
            .then(() => {
                successToast(intl.formatMessage({ id: "t005" }));
                clearSelection();
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const deleteIds = async (ids: string[]) => {
        await deleteAutocompletes({ ids: ids })
            .then(() => {
                clearSelection();
                successToast(intl.formatMessage({ id: "t005" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t006" }));
            });
    };

    const deleteAll = async () => {
        await deleteAutocompletes({
            keyword: pageParam.searchKeyword,
            searchType: pageParam.searchType,
        }).then(() => {
            clearSelection();
            successToast(intl.formatMessage({ id: "t005" }));
        })
        .catch(() => {
            errorToast(intl.formatMessage({ id: "t006" }));
        });
    };

    const onClickDeleteActionBtn = (e: React.MouseEvent) => {
        e.preventDefault();

        dialog.open({
            title: intl.formatMessage({ id: "i08008" }),
            content: intl.formatMessage({ id: "i08009" }),
            onConfirm: () => isAllSelected ? deleteAll() : deleteIds(selectedIds),
            confirmText: intl.formatMessage({ id: "b0008" }),
            variant: "danger",
        });
    };

    return (<>
        <AutocompleteModifyModal />

        <div role="table"
             className="selection-table grid-table-autocomplete"
        >
            <div role="rowgroup"
                 className="selection-table-thead"
            >
                <div role="columnheader"
                     className="selection-table-th"
                >
                    <IndeterminateCheckbox status={pageSelectionStatus}
                                           onChange={markPage}
                    />
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                >
                    {intl.formatMessage({ id: "i08002" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th"
                />
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {page?.content.map((autocomplete) => (
                    <AutocompleteFragment key={autocomplete.id}
                                          autocomplete={autocomplete}
                                          selectedIds={selectedIds}
                                          searchKeyword={pageParam.searchKeyword}
                                          searchType={pageParam.searchType}
                                          onCheck={(e, id) => markOne(id, e.target.checked)}
                                          onDelete={deleteOne}
                    />
                ))}
                {!page?.content?.length && (
                    <div className="empty-table">
                        {intl.formatMessage({ id: pageParam.searchKeyword ? "b0014" : "b0018" })}
                    </div>
                )}
            </div>

            <div className="col-span-full">
                <SelectionActionBar centerByParent
                                    selectionCount={selectionCount}
                                    selectAll={() => markAll(true)}
                                    deSelect={() => markAll(false)}
                                    remove={onClickDeleteActionBtn}
                />
            </div>
        </div>
    </>);
};

interface AutocompleteFragmentProps {
    autocomplete: AutoComplete;
    selectedIds: string[];
    searchKeyword?: string;
    searchType: "name" | "autocomplete_keyword";
    onCheck: (e: React.ChangeEvent<HTMLInputElement>, id: string) => void;
    onDelete: (id: string) => Promise<void>;
}

const AutocompleteFragment: React.FC<AutocompleteFragmentProps> = (props) => {
    const { autocomplete, selectedIds, searchKeyword, searchType, onCheck, onDelete } = props;

    const intl = useIntl();
    const agentId = useAgentId();
    const dialog = useDialog();
    const modal = useModal();
    
    const { moveToAutocompleteKeyword } = useRouteHandler();

    const onClick = () => {
        if (searchType === "autocomplete_keyword") {
            moveToAutocompleteKeyword(agentId, autocomplete.id, { state: { searchQuery: searchKeyword } });
        } else {
            moveToAutocompleteKeyword(agentId, autocomplete.id);
        }
    };

    const onClickUpdate = (e: React.MouseEvent) => {
        e.stopPropagation();
        modal.open(AUTOCOMPLETE_MODIFY_MODAL_ID, autocomplete);
    };

    const onClickDelete = (e: React.MouseEvent) => {
        e.stopPropagation();

        dialog.open({
            title: intl.formatMessage({ id: "i08005" }),
            content: intl.formatMessage({ id: "i08014" }),
            onConfirm: () => onDelete(autocomplete.id),
            confirmText: intl.formatMessage({ id: "b0008" }),
            variant: "danger"
        });
    };

    return (
        <div tabIndex={0}
             role="row"
             className="selection-table-tr group cursor-pointer"
             onClick={onClick}
             onKeyDown={clickFromKeyboard}
        >
            <div role="cell"
                 className="selection-table-td"
            >
                <input type="checkbox"
                       className={classNames(
                           selectedIds.length ? "opacity-100" : "opacity-0",
                           "selection-table-checkbox group-interaction"
                       )}
                       checked={selectedIds.includes(autocomplete.id)}
                       onClick={e => e.stopPropagation()}
                       onChange={e => onCheck(e, autocomplete.id)}
                />
            </div>
            <div role="cell"
                 className="selection-table-td"
            >
                <HighlightText query={autocomplete.name}
                               keyword={searchKeyword}
                               highlightClassName={classNames(
                                   searchType === "name" && "text-highlight"
                               )}
                />
            </div>
            <div role="cell"
                 className="selection-table-td"
            >
                <Popover>
                    {({ open, close }) => (
                        <>
                            <PopoverButton className="selection-table-popover-btn"
                                           open={open}
                                           close={close}
                            />
                            <PopoverMenu>
                                <PopoverItem onClick={onClickUpdate}
                                            className="selection-table-popover-item"
                                >
                                    <EditIcon className="size-3.5" />
                                    {intl.formatMessage({ id: "i08004" })}
                                </PopoverItem>
                                <PopoverItem onClick={onClickDelete}
                                            className="selection-table-popover-item text-point-red"
                                >
                                    <DeleteIcon className="size-3.5" />
                                    {intl.formatMessage({ id: "i08005" })}
                                </PopoverItem>
                            </PopoverMenu>
                        </>
                    )}
                </Popover>
            </div>
        </div>
    );
};

export default AutocompleteTable;
